import React, { useState, useRef, useEffect, useMemo } from 'react';
import {
    Sun, Moon, LogOut, Home, Users, Briefcase, Building, HardHat,
    Settings, FileText, Menu, CalendarClock, Database, ChevronRight, QrCode,
    ListChecks, Edit3, CalendarPlus, ClipboardList, UserCheck, Calculator, CalendarDays,
    UserCircle, Search, ChevronDown, ShieldCheck, BookOpen,
    Mail, MailPlus, ScrollText, FileSpreadsheet, Activity, FileBarChart, Radio,
    PanelLeft, PanelRight, PanelTop, PanelBottom, X, Layers, Cpu
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { apiClient } from '../utils/apiClient';
import companyLogo from '../assets/logo.png';
import { LEGAL_LINKS } from '../config/legal';

const NAV_POSITIONS = [
    { id: 'left', label: 'Sol', icon: PanelLeft, title: 'Menüyü Sola Sabitle' },
    { id: 'right', label: 'Sağ', icon: PanelRight, title: 'Menüyü Sağa Sabitle' },
    { id: 'top', label: 'Üst', icon: PanelTop, title: 'Menüyü Üste Sabitle (Tam Genişlik)' },
    { id: 'bottom', label: 'Alt', icon: PanelBottom, title: 'Menüyü Alta Sabitle (Dock Görünümü)' },
];

export default function Sidebar({
    currentPage,
    handleMenuItemClick,
    isSidebarCollapsed,
    toggleSidebar,
    darkMode,
    toggleDarkMode,
    sidebarRef,
    navPosition = 'left',
    onChangeNavPosition
}) {
    const { firmCode, fullName, handleLogout, userRole, permissions = [] } = useAuth();
    const [isReportWizardEnabled, setIsReportWizardEnabled] = useState(false);
    const [unreadSupportCount, setUnreadSupportCount] = useState(0);
    const [menuSearch, setMenuSearch] = useState('');
    const [openCategories, setOpenCategories] = useState({});
    const [hoveredCategory, setHoveredCategory] = useState(null);
    const [hoveredCategoryTop, setHoveredCategoryTop] = useState(80);
    const [openHorizontalCategory, setOpenHorizontalCategory] = useState(null);
    const [horizontalDropdownLeft, setHorizontalDropdownLeft] = useState(16);
    const [showPosPicker, setShowPosPicker] = useState(false);
    const hoverTimeoutRef = useRef(null);
    const horizontalHoverTimeoutRef = useRef(null);
    const horizontalNavRef = useRef(null);

    const isHorizontal = navPosition === 'top' || navPosition === 'bottom';
    const isRight = navPosition === 'right';

    useEffect(() => {
        const fetchUnreadCount = async () => {
            if (!permissions || !permissions.includes('support:access')) return;
            try {
                const res = await apiClient('TenantSupport/unread-count');
                if (res && res.count !== undefined) {
                    setUnreadSupportCount(res.count);
                }
            } catch (_) {
                // Sessizce atla
            }
        };

        fetchUnreadCount();
        const intervalId = setInterval(fetchUnreadCount, 30000);
        return () => clearInterval(intervalId);
    }, [permissions]);

    useEffect(() => {
        const checkFeatures = async () => {
            try {
                if (!permissions || !permissions.some(p => p.startsWith('reports:'))) return;
                const response = await apiClient('reports/custom/quota-status', { queryParams: { t: Date.now() } });
                if (response && response.isReportWizardEnabled) {
                    setIsReportWizardEnabled(true);
                }
            } catch (_) {
                // Sessizce atla
            }
        };
        checkFeatures();
    }, [permissions]);

    const menuItemsToRender = useMemo(() => {
        const allMenuItems = [
            {
                icon: Home,
                label: 'Ana Sayfa',
                page: 'dashboard',
                permission: null,
                accent: 'text-blue-500'
            },
            {
                id: 'pdks_ops',
                icon: CalendarClock,
                label: 'PDKS & Puantaj',
                accent: 'text-emerald-500',
                subItems: [
                    { icon: Calculator, label: 'Puantaj Kontrol', page: 'puantaj_control', permission: 'payroll:view_report' },
                    { icon: ListChecks, label: 'Geçiş Kayıtları', page: 'pass_records_list', permission: 'pass_records:view_list' },
                    { icon: Edit3, label: 'Manuel Kayıt', page: 'manual_pass_record', permission: 'pass_records:create_manual' },
                    { icon: CalendarPlus, label: 'İzin Yönetimi', page: 'leave_management', permission: 'leave_requests:view_list' },
                    { icon: ClipboardList, label: 'İzin Bakiyeleri', page: 'leave_balances', permission: 'leave_balances:view' },
                    { icon: UserCheck, label: 'Personel-Vardiya Atama', page: 'employee_assignment', permission: 'scheduling:assign' },
                    { icon: Layers, label: 'Toplu İşlemler', page: 'bulk_operations', permission: 'bulk_operations:access' }
                ]
            },
            {
                id: 'org_hr',
                icon: Users,
                label: 'Personel & Organizasyon',
                accent: 'text-indigo-500',
                subItems: [
                    { icon: Users, label: 'Personel Yönetimi', page: 'personnel', permission: 'personnel:view_list' },
                    { icon: Building, label: 'Bölüm Yönetimi', page: 'departments', permission: 'departments:view_list' },
                    { icon: HardHat, label: 'Pozisyon Yönetimi', page: 'positions', permission: 'positions:view_list' },
                    { icon: Building, label: 'Lokasyon Yönetimi', page: 'locations', permission: 'personnel:view_list' },
                    { icon: Briefcase, label: 'Çalışma Rejimi Yönetimi', page: 'working_regimes', permission: 'personnel:view_list' },
                    { icon: FileText, label: 'Duyurular', page: 'announcements', permission: 'announcements:manage' }
                ]
            },
            {
                id: 'iot_access',
                icon: Cpu,
                label: 'Cihaz & Geçiş (IoT)',
                accent: 'text-cyan-500',
                subItems: [
                    { icon: QrCode, label: 'Cihaz Yönetimi', page: 'devices', permission: 'devices:view_list' },
                    { icon: Radio, label: 'Cihaz Erişimleri', page: 'device_reachability', permission: 'devices:view_list' },
                    { icon: ShieldCheck, label: 'Yetki Grupları', page: 'access_groups', permission: 'access_groups:view_list' },
                    { icon: Database, label: 'Yetki Senkronu', page: 'access_sync', permission: 'access_sync:view' }
                ]
            },
            {
                id: 'rules_defs',
                icon: BookOpen,
                label: 'Kurallar & Tanımlamalar',
                accent: 'text-amber-500',
                subItems: [
                    { icon: CalendarClock, label: 'Vardiya ve Puantaj Ayarları', page: 'scheduling_definitions', permission: 'scheduling:view_list' },
                    { icon: ClipboardList, label: 'İzin Türü Yönetimi', page: 'leave_types', permission: 'leave_types:view_list' },
                    { icon: CalendarDays, label: 'Resmi Tatil Yönetimi', page: 'public_holidays', permission: 'public_holidays:view_list' },
                    { icon: CalendarDays, label: 'Özel Gün & Temalar', page: 'dashboard_special_days', permission: 'special_days:manage' },
                    { icon: ShieldCheck, label: 'Rol Yönetimi', page: 'role_management', permission: 'roles:view_list' }
                ]
            },
            {
                id: 'reports_import',
                icon: FileBarChart,
                label: 'Raporlar & Aktarım',
                accent: 'text-purple-500',
                subItems: [
                    {
                        icon: Database,
                        label: 'PDKS Raporları',
                        page: 'pdks_reports_page',
                        permissions: [
                            'reports:view_pdks',
                            'reports:view_puantaj_raporu',
                            'reports:view_monthly_attendance_matrix',
                            'reports:view_daily_attendance',
                            'reports:view_entry_exit_summary',
                            'reports:view_raw_transitions',
                            'reports:view_latecomers_report',
                            'reports:view_early_leavers_report',
                            'reports:view_absenteeism_report',
                            'reports:view_overtime_report',
                            'reports:view_leaves_report',
                            'reports:view_failed_pass_attempts'
                        ]
                    },
                    {
                        icon: Users,
                        label: 'Sicil Raporları',
                        page: 'personnel_reports_page',
                        permissions: [
                            'reports:view_personnel',
                            'reports:view_personnel_list',
                            'reports:view_personal_info',
                            'reports:view_legal_consent_report',
                            'reports:view_leave_balance_report',
                            'reports:view_terminated_employees'
                        ]
                    },
                    ...(isReportWizardEnabled ? [{ icon: FileBarChart, label: 'Özel Raporlar', page: 'custom_reports', permission: 'reports:manage_custom' }] : []),
                    { icon: FileSpreadsheet, label: 'Personel Aktarım', page: 'employee_import', permission: 'personnel:create' },
                    { icon: CalendarPlus, label: 'İzin Bakiye Aktarımı', page: 'leave_balance_import', permission: 'leave_balances:import' }
                ]
            },
            {
                id: 'system_settings',
                icon: Settings,
                label: 'Sistem & Ayarlar',
                accent: 'text-rose-500',
                subItems: [
                    { icon: Settings, label: 'Genel Ayarlar', page: 'settings', permission: 'settings:manage' },
                    { icon: Calculator, label: 'Otomatik Hesaplama Ayarları', page: 'payroll_calc_settings', permission: 'settings:manage' },
                    { icon: ShieldCheck, label: 'Güvenlik Ayarları', page: 'security_settings', permission: 'settings:manage' },
                    { icon: Mail, label: 'Mail Sistem Ayarları', page: 'mail_settings', permission: 'mail_settings:view' },
                    { icon: MailPlus, label: 'Mail Abonelikleri', page: 'mail_subscriptions', permission: 'mail_subscriptions:view' },
                    { icon: ScrollText, label: 'Mail Gönderim Logları', page: 'mail_dispatch_logs', permission: 'mail_dispatch_logs:view' },
                    { icon: Activity, label: 'Güvenlik Logları', page: 'security_audit_logs', permission: 'security:audit:view_list' }
                ]
            }
        ];

        const filterMenu = (items) => {
            return items.reduce((acc, item) => {
                if (item.subItems) {
                    const filteredSubItems = filterMenu(item.subItems);
                    if (filteredSubItems.length > 0) {
                        acc.push({ ...item, subItems: filteredSubItems });
                    }
                } else {
                    const needed = item.permissions || (item.permission == null ? [] : [item.permission]);
                    if (needed.length === 0 || needed.some(p => permissions.includes(p))) {
                        acc.push(item);
                    }
                }
                return acc;
            }, []);
        };

        return filterMenu(allMenuItems);
    }, [permissions, isReportWizardEnabled]);

    // Aktif sayfanın bulunduğu kategoriyi otomatik açık tut
    useEffect(() => {
        menuItemsToRender.forEach(cat => {
            if (cat.subItems && cat.subItems.some(sub => sub.page === currentPage)) {
                setOpenCategories(prev => ({ ...prev, [cat.id || cat.label]: true }));
            }
        });
    }, [currentPage, menuItemsToRender]);

    // Yatay modda dışarı tıklanınca açılır menüyü kapat
    useEffect(() => {
        const handleOutsideClick = (e) => {
            if (horizontalNavRef.current && !horizontalNavRef.current.contains(e.target)) {
                setOpenHorizontalCategory(null);
                setShowPosPicker(false);
            }
        };
        document.addEventListener('mousedown', handleOutsideClick);
        return () => document.removeEventListener('mousedown', handleOutsideClick);
    }, []);

    // Arama filtresi
    const filteredMenu = useMemo(() => {
        const q = menuSearch.trim().toLocaleLowerCase('tr-TR');
        if (!q) return menuItemsToRender;

        return menuItemsToRender.reduce((acc, item) => {
            if (!item.subItems) {
                if (item.label.toLocaleLowerCase('tr-TR').includes(q)) {
                    acc.push(item);
                }
                return acc;
            }
            const matchingSubs = item.subItems.filter(sub =>
                sub.label.toLocaleLowerCase('tr-TR').includes(q) ||
                item.label.toLocaleLowerCase('tr-TR').includes(q)
            );
            if (matchingSubs.length > 0) {
                acc.push({ ...item, subItems: matchingSubs, forceOpen: true });
            }
            return acc;
        }, []);
    }, [menuItemsToRender, menuSearch]);

    const toggleCategory = (catKey) => {
        setOpenCategories(prev => ({ ...prev, [catKey]: !prev[catKey] }));
    };

    const showSupportButton = permissions.includes('support:access');

    // ========================================================================
    // YATAY MOD (ÜST NAVBAR VEYA ALT DOCK NAVBAR)
    // ========================================================================
    if (isHorizontal) {
        const isBottom = navPosition === 'bottom';
        const activeHorizontalItem = menuItemsToRender.find(
            item => (item.id || item.label) === openHorizontalCategory && item.subItems && item.subItems.length > 0
        );

        const openHorizontalMenuForButton = (catKey, btnElement, toggleIfSame = false) => {
            clearTimeout(horizontalHoverTimeoutRef.current);
            if (toggleIfSame && openHorizontalCategory === catKey) {
                setOpenHorizontalCategory(null);
                return;
            }
            if (btnElement) {
                const rect = btnElement.getBoundingClientRect();
                const clampedLeft = Math.max(12, Math.min(rect.left, window.innerWidth - 276));
                setHorizontalDropdownLeft(clampedLeft);
            }
            setOpenHorizontalCategory(catKey);
        };

        return (
            <div
                ref={(node) => {
                    horizontalNavRef.current = node;
                    if (sidebarRef) sidebarRef.current = node;
                }}
            >
                <nav
                    className={`fixed inset-x-0 z-40 h-16 px-3 md:px-5 flex items-center justify-between gap-3 transition-all duration-300 ${
                        isBottom
                            ? 'bottom-0 border-t border-gray-200 dark:border-white/10 shadow-[0_-8px_30px_rgba(0,0,0,0.18)]'
                            : 'top-0 border-b border-gray-200 dark:border-white/10 shadow-md'
                    } ${darkMode ? 'bg-[#18191c] text-white' : 'bg-white text-gray-900'}`}
                >
                    {/* Sol: Logo */}
                    <div className="flex items-center gap-3 shrink-0">
                        <button
                            onClick={() => {
                                handleMenuItemClick('dashboard');
                                setOpenHorizontalCategory(null);
                            }}
                            className="flex items-center gap-2 focus:outline-none"
                            title="Ana Sayfa"
                        >
                            <img src={companyLogo} alt="Logo" className="h-7 w-auto" />
                        </button>
                    </div>

                    {/* Orta: Yatay Kategori Sekmeleri */}
                    <div className="flex items-center gap-1 overflow-x-auto hide-scrollbar py-1">
                        {menuItemsToRender.map((item) => {
                            const Icon = item.icon;
                            const catKey = item.id || item.label;
                            const hasSubs = item.subItems && item.subItems.length > 0;
                            const isActive = hasSubs
                                ? item.subItems.some(sub => sub.page === currentPage)
                                : item.page === currentPage;
                            const isDropdownOpen = openHorizontalCategory === catKey;

                            return (
                                <div
                                    key={catKey}
                                    className=" shrink-0"
                                    onMouseEnter={(e) => {
                                        if (hasSubs) {
                                            openHorizontalMenuForButton(catKey, e.currentTarget, false);
                                        } else {
                                            clearTimeout(horizontalHoverTimeoutRef.current);
                                            setOpenHorizontalCategory(null);
                                        }
                                    }}
                                    onMouseLeave={() => {
                                        if (hasSubs) {
                                            horizontalHoverTimeoutRef.current = setTimeout(() => {
                                                setOpenHorizontalCategory(prev => prev === catKey ? null : prev);
                                            }, 200);
                                        }
                                    }}
                                >
                                    <button
                                        type="button"
                                        onClick={(e) => {
                                            if (!hasSubs) {
                                                handleMenuItemClick(item.page);
                                                setOpenHorizontalCategory(null);
                                            } else {
                                                openHorizontalMenuForButton(catKey, e.currentTarget, true);
                                            }
                                        }}
                                        className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold whitespace-nowrap transition-all ${
                                            isActive
                                                ? 'bg-emerald-600 text-white shadow-sm shadow-emerald-600/25'
                                                : isDropdownOpen
                                                    ? 'bg-gray-100 dark:bg-gray-700 text-emerald-600 dark:text-emerald-400'
                                                    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700/70'
                                        }`}
                                    >
                                        <Icon className={`w-4 h-4 shrink-0 ${isActive ? 'text-white' : item.accent}`} />
                                        <span>{item.label}</span>
                                        {hasSubs && (
                                            <ChevronDown className={`w-3.5 h-3.5 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} />
                                        )}
                                    </button>
                                </div>
                            );
                        })}
                    </div>

                    {/* Sağ: Hızlı Araçlar (Destek, Konum Seçici, Tema, Profil, Çıkış) */}
                    <div className="flex items-center gap-1.5 shrink-0">
                        {showSupportButton && (
                            <button
                                onClick={() => handleMenuItemClick('support_tickets')}
                                className="relative p-2 rounded-xl bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 hover:bg-emerald-100 dark:hover:bg-emerald-900/50 transition-colors"
                                title="Destek ve Yardım"
                            >
                                <Activity className="w-4 h-4" />
                                {unreadSupportCount > 0 && (
                                    <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] font-bold w-4 h-4 flex items-center justify-center rounded-full">
                                        {unreadSupportCount > 9 ? '9+' : unreadSupportCount}
                                    </span>
                                )}
                            </button>
                        )}

                        {/* Hızlı Menü Konumu Değiştirici */}
                        <div className="relative">
                            <button
                                type="button"
                                onClick={() => setShowPosPicker(p => !p)}
                                className="p-2 rounded-xl bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                                title="Menü Konumunu Değiştir (Sol / Sağ / Üst / Alt)"
                            >
                                {navPosition === 'top' ? <PanelTop className="w-4 h-4" /> : <PanelBottom className="w-4 h-4" />}
                            </button>
                        </div>

                        {/* Tema Butonu */}
                        <button
                            onClick={toggleDarkMode}
                            className="p-2 rounded-xl bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                            title={darkMode ? 'Aydınlık Moda Geç' : 'Karanlık Moda Geç'}
                        >
                            {darkMode ? <Moon className="w-4 h-4 text-[#e30613]" /> : <Sun className="w-4 h-4 text-amber-500" />}
                        </button>

                        {/* Kullanıcı Profili */}
                        <button
                            onClick={() => handleMenuItemClick('user_profile')}
                            className="hidden sm:flex items-center gap-2 px-2.5 py-1.5 rounded-xl bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors text-gray-900 dark:text-white"
                            title="Kullanıcı Profili"
                        >
                            <div className="w-6 h-6 rounded-full bg-emerald-600 text-white text-xs font-bold flex items-center justify-center">
                                {fullName ? fullName[0].toUpperCase() : (firmCode ? firmCode[0].toUpperCase() : 'U')}
                            </div>
                            <span className="text-xs font-semibold max-w-[100px] truncate">{fullName || firmCode}</span>
                        </button>

                        {/* Çıkış Yap */}
                        <button
                            onClick={handleLogout}
                            className="p-2 rounded-xl bg-red-50 dark:bg-red-950/40 text-red-600 dark:text-red-400 hover:bg-red-600 hover:text-white transition-colors"
                            title="Çıkış Yap"
                        >
                            <LogOut className="w-4 h-4" />
                        </button>
                    </div>
                </nav>

                {/* YATAY MOD AÇILIR ALT MENÜ (OVERFLOW DIŞINDA SABİT PORTAL) */}
                {activeHorizontalItem && (
                    <div
                        onMouseEnter={() => clearTimeout(horizontalHoverTimeoutRef.current)}
                        onMouseLeave={() => {
                            horizontalHoverTimeoutRef.current = setTimeout(() => {
                                setOpenHorizontalCategory(null);
                            }, 180);
                        }}
                        style={{ left: `${horizontalDropdownLeft}px` }}
                        className={`fixed ${
                            isBottom ? 'bottom-[68px]' : 'top-[68px]'
                        } w-64 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl shadow-2xl p-2 z-50 space-y-1`}
                    >
                        <div className="px-2.5 py-1.5 text-[11px] font-bold uppercase tracking-wider text-gray-400 dark:text-gray-400 border-b border-gray-100 dark:border-gray-700 mb-1">
                            {activeHorizontalItem.label}
                        </div>
                        {activeHorizontalItem.subItems.map(sub => {
                            const SubIcon = sub.icon;
                            const subActive = currentPage === sub.page;
                            return (
                                <button
                                    key={sub.page}
                                    type="button"
                                    onClick={() => {
                                        handleMenuItemClick(sub.page);
                                        setOpenHorizontalCategory(null);
                                    }}
                                    className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-xl text-xs font-medium transition-colors ${
                                        subActive
                                            ? 'bg-emerald-600 text-white font-semibold shadow-sm'
                                            : 'text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700'
                                    }`}
                                >
                                    <SubIcon className={`w-4 h-4 shrink-0 ${subActive ? 'text-white' : activeHorizontalItem.accent}`} />
                                    <span className="truncate">{sub.label}</span>
                                </button>
                            );
                        })}
                    </div>
                )}

                {/* HIZLI MENÜ KONUMU SEÇİCİ POPOVER (OVERFLOW DIŞINDA) */}
                {showPosPicker && (
                    <div
                        className={`fixed right-4 ${
                            isBottom ? 'bottom-[68px]' : 'top-[68px]'
                        } bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl shadow-2xl p-2.5 z-50 w-48`}
                    >
                        <div className="text-[10px] font-bold uppercase text-gray-400 px-2 py-1 mb-1">Menü Konumu</div>
                        <div className="grid grid-cols-2 gap-1.5">
                            {NAV_POSITIONS.map(pos => {
                                const PosIcon = pos.icon;
                                const active = navPosition === pos.id;
                                return (
                                    <button
                                        key={pos.id}
                                        type="button"
                                        onClick={() => {
                                            onChangeNavPosition?.(pos.id);
                                            setShowPosPicker(false);
                                        }}
                                        className={`flex items-center gap-1.5 px-2.5 py-2 rounded-xl text-xs font-semibold transition-colors ${
                                            active
                                                ? 'bg-emerald-600 text-white shadow-sm'
                                                : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                                        }`}
                                    >
                                        <PosIcon className="w-3.5 h-3.5" />
                                        {pos.label}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                )}
            </div>
        );
    }

    // ========================================================================
    // DİKEY MOD (SOL SİDEBAR VEYA SAĞ SİDEBAR)
    // ========================================================================
    return (
        <aside
            ref={sidebarRef}
            className={`fixed inset-y-0 ${isRight ? 'right-0 rounded-l-2xl border-l' : 'left-0 rounded-r-2xl border-r'} z-40 ${
                darkMode ? 'bg-[#18191c] border-white/5' : 'bg-white border-gray-200'
            } text-gray-900 dark:text-white flex flex-col p-3.5 shadow-xl transition-all duration-300 ${
                isSidebarCollapsed ? 'w-[72px] items-center' : 'w-[260px]'
            }`}
        >
            {/* Üst Başlık: Logo & Daralt/Genişlet Butonu */}
            <div className={`mb-3 pb-3 border-b border-gray-200 dark:border-gray-700/80 flex items-center ${isSidebarCollapsed ? 'justify-center' : 'justify-between'} px-1 h-12 shrink-0`}>
                {isSidebarCollapsed ? (
                    <button
                        onClick={toggleSidebar}
                        className="text-[#e30613] text-3xl font-black drop-shadow-[0_0_8px_rgba(227,6,19,0.35)] select-none hover:scale-105 transition-transform"
                        style={{ fontFamily: "'Orbitron', sans-serif" }}
                        title="Menüyü Genişlet"
                    >
                        X
                    </button>
                ) : (
                    <>
                        <img
                            src={companyLogo}
                            alt="Şirket Logosu"
                            className="h-8 w-auto cursor-pointer"
                            onClick={() => handleMenuItemClick('dashboard')}
                        />
                        <button
                            onClick={toggleSidebar}
                            className="p-1.5 rounded-lg text-gray-400 hover:text-gray-700 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700/70 transition-colors"
                            title="Menüyü Daralt"
                        >
                            <Menu className="w-4 h-4" />
                        </button>
                    </>
                )}
            </div>

            {/* Menü İçi Hızlı Arama Çubuğu (Açıkken) */}
            {!isSidebarCollapsed && (
                <div className="relative mb-3 shrink-0">
                    <Search className="w-3.5 h-3.5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                    <input
                        type="text"
                        value={menuSearch}
                        onChange={(e) => setMenuSearch(e.target.value)}
                        placeholder="Menüde hızlı ara..."
                        className="w-full pl-8 pr-7 py-1.5 text-xs rounded-xl bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                    />
                    {menuSearch && (
                        <button
                            type="button"
                            onClick={() => setMenuSearch('')}
                            className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                        >
                            <X className="w-3.5 h-3.5" />
                        </button>
                    )}
                </div>
            )}

            {/* Ana Navigasyon (Akordiyon + Daraltılmış Popover) */}
            <nav className="flex-1 overflow-y-auto hide-scrollbar space-y-1 w-full pr-0.5">
                <ul className="space-y-1">
                    {filteredMenu.map((item) => {
                        const Icon = item.icon;
                        const catKey = item.id || item.label;
                        const hasSubItems = item.subItems && item.subItems.length > 0;
                        const isActive = hasSubItems
                            ? item.subItems.some(sub => sub.page === currentPage)
                            : item.page === currentPage;
                        const isExpanded = item.forceOpen || !!openCategories[catKey];

                        if (!hasSubItems) {
                            return (
                                <li key={catKey}>
                                    <button
                                        type="button"
                                        onClick={() => handleMenuItemClick(item.page)}
                                        className={`relative group w-full flex items-center px-3 py-2.5 rounded-xl text-xs font-semibold transition-all ${
                                            isActive
                                                ? 'bg-emerald-600 text-white shadow-sm shadow-emerald-600/25'
                                                : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700/60'
                                        } ${isSidebarCollapsed ? 'justify-center px-0' : 'justify-start'}`}
                                        title={isSidebarCollapsed ? item.label : undefined}
                                    >
                                        <Icon className={`w-4 h-4 shrink-0 ${isSidebarCollapsed ? '' : 'mr-2.5'} ${isActive ? 'text-white' : item.accent}`} />
                                        {!isSidebarCollapsed && <span className="truncate">{item.label}</span>}
                                    </button>
                                </li>
                            );
                        }

                        return (
                            <li
                                key={catKey}
                                className="relative"
                                onMouseEnter={(e) => {
                                    if (!isSidebarCollapsed) return;
                                    clearTimeout(hoverTimeoutRef.current);
                                    const rect = e.currentTarget.getBoundingClientRect();
                                    const clampedTop = Math.max(12, Math.min(rect.top, window.innerHeight - 280));
                                    setHoveredCategoryTop(clampedTop);
                                    setHoveredCategory(catKey);
                                }}
                                onMouseLeave={() => {
                                    if (!isSidebarCollapsed) return;
                                    hoverTimeoutRef.current = setTimeout(() => setHoveredCategory(null), 180);
                                }}
                            >
                                {/* Kategori Başlığı */}
                                <button
                                    type="button"
                                    onClick={() => {
                                        if (isSidebarCollapsed) {
                                            toggleSidebar();
                                            setOpenCategories(prev => ({ ...prev, [catKey]: true }));
                                        } else {
                                            toggleCategory(catKey);
                                        }
                                    }}
                                    className={`w-full flex items-center justify-between px-3 py-2.5 rounded-xl text-xs font-semibold transition-all ${
                                        isActive
                                            ? 'bg-gray-100 dark:bg-gray-800/90 text-emerald-600 dark:text-emerald-400 border border-emerald-500/25'
                                            : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700/60'
                                    } ${isSidebarCollapsed ? 'justify-center px-0' : ''}`}
                                >
                                    <div className="flex items-center min-w-0">
                                        <Icon className={`w-4 h-4 shrink-0 ${isSidebarCollapsed ? '' : 'mr-2.5'} ${item.accent}`} />
                                        {!isSidebarCollapsed && <span className="truncate">{item.label}</span>}
                                    </div>
                                    {!isSidebarCollapsed && (
                                        <ChevronRight
                                            className={`w-3.5 h-3.5 text-gray-400 transition-transform duration-200 shrink-0 ${
                                                isExpanded ? 'rotate-90 text-emerald-500' : ''
                                            }`}
                                        />
                                    )}
                                </button>

                                {/* Açıkken: Aşağı Doğru Akordiyon Alt Menü (Asla ekran dışına taşmaz) */}
                                {!isSidebarCollapsed && isExpanded && (
                                    <ul className="mt-1 ml-3.5 pl-2.5 border-l border-gray-200 dark:border-gray-700 space-y-0.5">
                                        {item.subItems.map((subItem) => {
                                            const SubIcon = subItem.icon;
                                            const subActive = currentPage === subItem.page;
                                            return (
                                                <li key={subItem.page}>
                                                    <button
                                                        type="button"
                                                        onClick={() => handleMenuItemClick(subItem.page)}
                                                        className={`w-full flex items-center gap-2 px-2.5 py-2 rounded-lg text-xs font-medium transition-all ${
                                                            subActive
                                                                ? 'bg-emerald-600 text-white font-semibold shadow-sm shadow-emerald-600/20'
                                                                : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700/50 hover:text-gray-900 dark:hover:text-white'
                                                        }`}
                                                    >
                                                        <SubIcon className={`w-3.5 h-3.5 shrink-0 ${subActive ? 'text-white' : item.accent}`} />
                                                        <span className="truncate">{subItem.label}</span>
                                                    </button>
                                                </li>
                                            );
                                        })}
                                    </ul>
                                )}

                                {/* Daraltılmışken (72px): Yan Popover Alt Menü */}
                                {isSidebarCollapsed && hoveredCategory === catKey && (
                                    <div
                                        className={`fixed ${isRight ? 'right-[76px]' : 'left-[76px]'} w-56 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl shadow-2xl p-2 z-50 space-y-1`}
                                        style={{ top: `${hoveredCategoryTop}px`, maxHeight: '75vh', overflowY: 'auto' }}
                                    >
                                        <div className="px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider text-gray-400 border-b border-gray-100 dark:border-gray-700 mb-1">
                                            {item.label}
                                        </div>
                                        {item.subItems.map(subItem => {
                                            const SubIcon = subItem.icon;
                                            const subActive = currentPage === subItem.page;
                                            return (
                                                <button
                                                    key={subItem.page}
                                                    type="button"
                                                    onClick={() => {
                                                        handleMenuItemClick(subItem.page);
                                                        setHoveredCategory(null);
                                                    }}
                                                    className={`w-full flex items-center gap-2 px-2.5 py-2 rounded-xl text-xs font-medium transition-colors ${
                                                        subActive
                                                            ? 'bg-emerald-600 text-white font-semibold'
                                                            : 'text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700'
                                                    }`}
                                                >
                                                    <SubIcon className={`w-3.5 h-3.5 shrink-0 ${subActive ? 'text-white' : item.accent}`} />
                                                    <span className="truncate">{subItem.label}</span>
                                                </button>
                                            );
                                        })}
                                    </div>
                                )}
                            </li>
                        );
                    })}
                </ul>
            </nav>

            {/* Alt Kısım: Kompakt Destek, Menü Konumu Seçici, Tema, Profil & Çıkış */}
            <div className="mt-auto pt-3 border-t border-gray-200 dark:border-gray-700/80 flex flex-col gap-2 w-full shrink-0">
                {/* Destek ve Yardım */}
                {showSupportButton && (
                    <button
                        onClick={() => handleMenuItemClick('support_tickets')}
                        className={`relative flex items-center justify-center font-semibold rounded-xl shadow-sm transition-all text-xs ${
                            unreadSupportCount > 0 ? 'animate-pulse' : ''
                        } ${isSidebarCollapsed ? 'w-10 h-10 mx-auto p-0' : 'w-full py-2 px-3'} bg-gradient-to-r from-teal-500 to-emerald-600 hover:from-teal-600 hover:to-emerald-700 text-white`}
                        title="Destek ve Yardım"
                    >
                        <Activity className={`w-4 h-4 ${isSidebarCollapsed ? '' : 'mr-2'}`} />
                        {!isSidebarCollapsed && <span>Destek ve Yardım</span>}
                        {unreadSupportCount > 0 && (
                            <span className="absolute -top-1.5 -right-1.5 bg-red-500 text-white text-[10px] font-bold w-4 h-4 flex items-center justify-center rounded-full shadow">
                                {unreadSupportCount > 9 ? '9+' : unreadSupportCount}
                            </span>
                        )}
                    </button>
                )}

                {/* Hızlı Menü Konumu (Sol / Sağ / Üst / Alt) & Tema Satırı */}
                {!isSidebarCollapsed ? (
                    <div className="flex items-center justify-between gap-1.5 bg-gray-100 dark:bg-[#111214] p-1 rounded-xl border border-gray-200 dark:border-[#212226]">
                        <div className="flex items-center gap-0.5">
                            {NAV_POSITIONS.map(pos => {
                                const PosIcon = pos.icon;
                                const active = navPosition === pos.id;
                                return (
                                    <button
                                        key={pos.id}
                                        type="button"
                                        onClick={() => onChangeNavPosition?.(pos.id)}
                                        title={pos.title}
                                        className={`p-1.5 rounded-lg text-[11px] font-semibold flex items-center gap-1 transition-all ${
                                            active
                                                ? 'bg-white dark:bg-[#18191c] text-emerald-600 dark:text-emerald-400 shadow-sm'
                                                : 'text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                                        }`}
                                    >
                                        <PosIcon className="w-3.5 h-3.5" />
                                    </button>
                                );
                            })}
                        </div>

                        <button
                            type="button"
                            onClick={toggleDarkMode}
                            className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-white dark:bg-[#18191c] border border-black/5 dark:border-white/5 text-xs font-semibold text-gray-700 dark:text-gray-200 shadow-sm"
                            title={darkMode ? 'Aydınlık Moda Geç' : 'Karanlık Moda Geç'}
                        >
                            {darkMode ? (
                                <>
                                    <Moon className="w-3.5 h-3.5 text-[#e30613]" />
                                    <span>Dark</span>
                                </>
                            ) : (
                                <>
                                    <Sun className="w-3.5 h-3.5 text-amber-500" />
                                    <span>Light</span>
                                </>
                            )}
                        </button>
                    </div>
                ) : (
                    <button
                        onClick={toggleDarkMode}
                        className="w-10 h-10 mx-auto flex items-center justify-center rounded-xl bg-gray-100 dark:bg-[#111214] border border-gray-200 dark:border-white/5 text-gray-700 dark:text-[#e30613]"
                        title={darkMode ? 'Aydınlık Moda Geç' : 'Karanlık Moda Geç'}
                    >
                        {darkMode ? <Moon className="w-4 h-4" /> : <Sun className="w-4 h-4 text-amber-500" />}
                    </button>
                )}

                {/* Kompakt Kullanıcı Kartı + Çıkış Yap İkonu */}
                <div className={`flex items-center ${isSidebarCollapsed ? 'flex-col gap-1.5' : 'justify-between gap-2 bg-gray-50 dark:bg-gray-800/80 border border-gray-200 dark:border-gray-700/80 p-2 rounded-xl'}`}>
                    <div
                        onClick={() => handleMenuItemClick('user_profile')}
                        className="flex items-center gap-2 min-w-0 cursor-pointer flex-1"
                        title="Kullanıcı Profili"
                    >
                        <div className="w-8 h-8 rounded-full bg-emerald-600 flex items-center justify-center text-white text-xs font-bold shrink-0">
                            {fullName ? fullName[0].toUpperCase() : (firmCode ? firmCode[0].toUpperCase() : <UserCircle size={18} />)}
                        </div>
                        {!isSidebarCollapsed && (
                            <div className="min-w-0 flex-1">
                                <p className="text-xs font-semibold text-gray-900 dark:text-white truncate">
                                    {fullName || firmCode}
                                </p>
                                <p className="text-[10px] text-gray-500 dark:text-gray-400 truncate">
                                    {userRole}
                                </p>
                            </div>
                        )}
                    </div>

                    <button
                        type="button"
                        onClick={handleLogout}
                        className="p-2 rounded-lg text-gray-500 dark:text-gray-400 hover:bg-red-600 hover:text-white transition-colors shrink-0"
                        title="Çıkış Yap"
                    >
                        <LogOut className="w-4 h-4" />
                    </button>
                </div>

                {/* Yasal Linkler */}
                {!isSidebarCollapsed && (
                    <div className="flex justify-center gap-1.5 text-[10px] text-gray-400 dark:text-gray-500 pt-0.5">
                        <a href={LEGAL_LINKS.PRIVACY_POLICY} target="_blank" rel="noopener noreferrer" className="hover:underline">Gizlilik</a>
                        <span>•</span>
                        <a href={LEGAL_LINKS.KVKK_AYDINLATMA} target="_blank" rel="noopener noreferrer" className="hover:underline">KVKK</a>
                        <span>•</span>
                        <a href={LEGAL_LINKS.SUPPORT} target="_blank" rel="noopener noreferrer" className="hover:underline">Destek</a>
                    </div>
                )}
            </div>
        </aside>
    );
}
